"use client";

import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  FaHome,
  FaChartBar,
  FaBook,
  FaChevronDown,
  FaChevronRight,
  FaSort,
  FaTree,
  FaProjectDiagram,
  FaRoute,
  FaTimes,
  FaClock,
  FaSitemap,
  FaInfoCircle,
  FaComment,
  FaStar,
  FaRegStar,
} from "react-icons/fa";
import "../styles/MainSidebar.css";
import { slugForRoute } from "../../../data/algorithms";
import {
  useGetFavouritesQuery,
  useToggleFavouriteMutation,
} from "../../../redux/slices/api/favouriteSlice";
import { useAuthModal } from "../../auth/AuthModalContext";
import { GiPathDistance, GiTreeGrowth } from "react-icons/gi";
import {
  HiOutlineDocumentSearch,
  HiOutlineLink,
  HiOutlineSwitchHorizontal,
} from "react-icons/hi";
import { BiHash } from "react-icons/bi";
import { TbRegex, TbStack, TbCube3dSphere } from "react-icons/tb";
import { MdOutlinePattern } from "react-icons/md";

const navSections = [
  {
    items: [
      {
        label: "Back to Website",
        link: "https://www.sakec.ac.in/",
        icon: <FaHome />,
      },
    ],
  },
  {
    title: "Visualize",
    items: [
      {
        label: "Visualization",
        link: "/visualization",
        icon: <FaChartBar />,
        subItems: [
          { label: "Sorting Algorithms",        link: "/visualization/sorting",          icon: <FaSort /> },
          { label: "Merge Sort 3D ✨",          link: "/visualization/3d/merge-sort",    icon: <TbCube3dSphere /> },
          { label: "Stack & Queue",             link: "/visualization/stack-queue",      icon: <TbStack /> },
          { label: "Linked List",               link: "/visualization/linkedlist",       icon: <HiOutlineLink /> },
          { label: "Travelling Salesman",       link: "/visualization/travelling-salesman", icon: <GiPathDistance /> },
          { label: "Longest Chain Subsequence", link: "/visualization/lcs",             icon: <HiOutlineSwitchHorizontal /> },
          {
            label: "Shortest Path",
            link: "/visualization/shortest-path",
            icon: <FaRoute />,
            subItems: [
              { label: "BFS", link: "/visualization/shortest-path/bfs", icon: <FaProjectDiagram /> },
              { label: "DFS", link: "/visualization/shortest-path/dfs", icon: <FaSitemap /> },
            ],
          },
          {
            label: "Trees",
            link: "/visualization/trees",
            icon: <FaTree />,
            subItems: [
              { label: "AVL Trees",      link: "/visualization/trees/avl-trees",      icon: <FaTree /> },
              { label: "Tree Traversal", link: "/visualization/trees/tree-traversal", icon: <FaProjectDiagram /> },
              { label: "Red Black Tree", link: "/visualization/trees/red-black-tree", icon: <GiTreeGrowth /> },
            ],
          },
          {
            label: "String Pattern Matching",
            link: "/visualization/string",
            icon: <TbRegex />,
            subItems: [
              { label: "Naive String Matching", link: "/visualization/string/naive-string",  icon: <HiOutlineDocumentSearch /> },
              { label: "Rabin Karp",            link: "/visualization/string/rabin-karp",    icon: <BiHash /> },
              { label: "Boyer Moore",           link: "/visualization/string/boyer-moore",   icon: <MdOutlinePattern /> },
            ],
          },
        ],
      },
    ],
  },
  {
    title: "Learn",
    items: [
      { label: "Notes",           link: "/notes", icon: <FaBook /> },
      { label: "Time Complexity", link: "/time",  icon: <FaClock /> },
    ],
  },
  {
    title: "More",
    items: [
      { label: "About Us", link: "/about",                             icon: <FaInfoCircle /> },
      { label: "Feedback", link: "https://forms.gle/Gcn9mcKUvH4fANAB9", icon: <FaComment /> },
    ],
  },
];

// Star toggle shown on leaf algorithm rows. Renders nothing for non-algorithm
// links (slug === null). Logged-out clicks redirect to /login.
function FavStar({ route }) {
  const slug = slugForRoute(route);
  const { user } = useSelector((state) => state.auth);
  const { openAuth } = useAuthModal();
  const { data } = useGetFavouritesQuery(undefined, { skip: !user });
  const [toggleFavourite] = useToggleFavouriteMutation();

  if (!slug) return null;

  const isFav = (data?.favourites ?? []).some((f) => f.algorithmSlug === slug);

  const handleClick = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) {
      openAuth("login");
      return;
    }
    try {
      await toggleFavourite(slug).unwrap();
    } catch (err) {
      // swallow — UI reflects server state via cache invalidation
    }
  };

  return (
    <button
      type="button"
      className={`fav-star ${isFav ? "active" : ""}`}
      onClick={handleClick}
      title={isFav ? "Remove from favourites" : "Add to favourites"}
      aria-label={isFav ? "Remove from favourites" : "Add to favourites"}
    >
      {isFav ? <FaStar /> : <FaRegStar />}
    </button>
  );
}

function MainSidebar({ closeSidebar }) {
  const location = useLocation();

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = "auto"; };
  }, []);

  function NavLink({ el }) {
    const isExternal = el.link.startsWith("http");
    const shouldBeOpen = location.pathname.startsWith(el.link) && el.link !== "/";
    const [isOpen, setIsOpen] = useState(shouldBeOpen);
    const hasSubItemWithCurrentPath = el.subItems?.some(
      (sub) =>
        location.pathname.startsWith(sub.link) ||
        sub.subItems?.some((n) => location.pathname === n.link),
    );
    const [nestedOpen, setNestedOpen] = useState({});

    useEffect(() => {
      if (el.subItems) {
        const init = {};
        el.subItems.forEach((sub) => {
          if (sub.subItems) init[sub.label] = location.pathname.startsWith(sub.link);
        });
        setNestedOpen(init);
      }
    }, [el.subItems, location.pathname]);

    const handleClick = () => {
      if (el.subItems) setIsOpen(!isOpen);
      else if (closeSidebar) closeSidebar();
    };

    const handleSubItemClick = (subItem, e) => {
      if (subItem.subItems) {
        e.preventDefault();
        setNestedOpen({ ...nestedOpen, [subItem.label]: !nestedOpen[subItem.label] });
      } else if (closeSidebar) {
        closeSidebar();
      }
    };

    if (el.subItems) {
      return (
        <div className="nav-item">
          <button
            className={`nav-link ${hasSubItemWithCurrentPath ? "active" : ""}`}
            onClick={handleClick}
          >
            <span className="nav-icon">{el.icon}</span>
            <span className="nav-label">{el.label}</span>
            {isOpen ? <FaChevronDown className="nav-chevron" /> : <FaChevronRight className="nav-chevron" />}
          </button>

          <div className={`sub-items ${isOpen ? "open" : ""}`}>
            {el.subItems.map((subItem) => (
              <div key={subItem.link} className="nav-item">
                {subItem.subItems ? (
                  <>
                    <button
                      className={`nav-link sub-link ${location.pathname.startsWith(subItem.link) ? "active" : ""}`}
                      onClick={(e) => handleSubItemClick(subItem, e)}
                    >
                      <span className="nav-icon">{subItem.icon}</span>
                      <span className="nav-label">{subItem.label}</span>
                      {nestedOpen[subItem.label]
                        ? <FaChevronDown className="nav-chevron" />
                        : <FaChevronRight className="nav-chevron" />}
                    </button>
                    <div className={`sub-items nested ${nestedOpen[subItem.label] ? "open" : ""}`}>
                      {subItem.subItems.map((nested) => (
                        <div key={nested.link} className="nav-item">
                          <Link
                            to={nested.link}
                            className={`nav-link sub-link nested ${location.pathname === nested.link ? "active" : ""}`}
                            onClick={closeSidebar}
                          >
                            <span className="nav-icon">{nested.icon}</span>
                            <span className="nav-label">{nested.label}</span>
                          </Link>
                          <FavStar route={nested.link} />
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <>
                    <Link
                      to={subItem.link}
                      className={`nav-link sub-link ${location.pathname === subItem.link ? "active" : ""}`}
                      onClick={closeSidebar}
                    >
                      <span className="nav-icon">{subItem.icon}</span>
                      <span className="nav-label">{subItem.label}</span>
                    </Link>
                    <FavStar route={subItem.link} />
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      );
    }

    return isExternal ? (
      <a href={el.link} target="_blank" rel="noopener noreferrer" className="nav-link" onClick={closeSidebar}>
        <span className="nav-icon">{el.icon}</span>
        <span className="nav-label">{el.label}</span>
      </a>
    ) : (
      <Link
        to={el.link}
        className={`nav-link ${location.pathname === el.link ? "active" : ""}`}
        onClick={closeSidebar}
      >
        <span className="nav-icon">{el.icon}</span>
        <span className="nav-label">{el.label}</span>
      </Link>
    );
  }

  return (
    <>
      <div className="sidebar-backdrop" onClick={closeSidebar} />
      <div className="sidebar">
        <div className="sidebar-header">
          <div className="sidebar-brand">
            <div className="sidebar-brand-icon">
              <FaChartBar />
            </div>
            <span className="sidebar-brand-dsa">DSA</span>
            <span className="sidebar-brand-dot">·</span>
            <span className="sidebar-brand-visualizer">Visualizer</span>
          </div>
          <button className="close-sidebar" onClick={closeSidebar} aria-label="Close sidebar">
            <FaTimes />
          </button>
        </div>

        <nav className="sidebar-nav">
          {navSections.map((section, sIdx) => (
            <div key={sIdx} className="sidebar-section">
              {section.title && (
                <div className="sidebar-section-title">{section.title}</div>
              )}
              {section.items.map((link) => (
                <NavLink key={link.label} el={link} />
              ))}
            </div>
          ))}
        </nav>
      </div>
    </>
  );
}

export default MainSidebar;
